// frontend/src/pages/ProjectCashFlows.tsx
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjectResults } from "@/utils/use-project-results";
import { formatNumber } from '@/utils/format';
import { useEffect, useState } from "react";
import CashFlowChart from "@/components/charts/CashFlowChart";

export default function ProjectCashFlows() {
  
  const { id } = useParams();
  const { results, loading, error } = useProjectResults(id);

  const { t } = useTranslation();

  if (loading) return <div className="p-4 text-xs">{t("common.loading")}</div>;
  if (error || !results) return <div className="p-4 text-elani-orange text-xs">{t("common.error_loading_data")}</div>;

  // Prepare chart data from results
  const chartData = results.revenue?.map((item, index) => ({
    year: item.Year,
    operatingCashFlow: results.revenue[index]["Revenue (EUR)"] - results.opex[index]["OPEX (EUR)"] - (results.taxes?.[index]?.["Taxes (EUR)"] || 0),
    investmentCashFlow: -(results.investment?.[index]?.["Investment (EUR)"] || 0),
    financingCashFlow: (results.net_debt?.[index]?.["Net Debt (EUR)"] ?? 0) - (results.debt_service[index]["Debt Service (EUR)"] || 0),
    cashFlowLeveredPostTax: results.cashflow?.[index]?.["Cash Flow (EUR)"] || 0,
    cashFlowLeveredPreTax: results.cashflow?.[index]?.["Cash Flow (levered, pre-tax) (EUR)"] || 0,
    cumulativeCashFlow: results.cashflow?.[index]?.["Cumulative Cash Flow (EUR)"] || 0,
  })) || [];

  return (
    <div className="overflow-x-auto max-w-6xl text-xs space-y-8">

      {/* Charts Section */}
      <CashFlowChart data={chartData} />

      {/* Detailed Table */}
      <div className="bg-white rounded-xl shadow-sm border border-border p-6">
        <div className="mb-6">
          <h3 className="text-base font-semibold mb-2">{t("project.cashflows.detailed_table")}</h3>
          <p className="text-xs text-gray-500">{t("project.cashflows.all_values_in_eur")}</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-xs">
            <colgroup>
              <col className="w-16" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
              <col className="w-[calc((100%-4rem)/9)]" />
            </colgroup>
            <thead className="bg-gray-200">
              <tr>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.year")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.revenue")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.opex")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.debt_service")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.profit")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.investment")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.net_debt")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.taxes")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.cash_flow")}</th>
                <th className='text-left font-semibold px-2 py-2'>{t("project.cashflows.columns.cumulative_cf")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {results.revenue.map((_, index) => (
                <tr key={index} className="hover:bg-gray-100 transition-colors">
                  <td className="px-2 py-1">{results.revenue[index].Year}</td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-2 py-0.5 rounded">
                      {formatNumber(results.revenue[index]["Revenue (EUR)"], 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-2 py-0.5 rounded">
                      {formatNumber(results.opex[index]["OPEX (EUR)"], 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-2 py-0.5 rounded">
                      {formatNumber(results.debt_service[index]["Debt Service (EUR)"], 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-2 py-0.5 rounded">
                      {formatNumber(results.profit?.[index]?.["Profit (EUR)"] ?? 0, 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-2 py-0.5 rounded">
                      {formatNumber(results.investment?.[index]?.["Investment (EUR)"] ?? 0, 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-2 py-0.5 rounded">
                      {formatNumber(results.net_debt?.[index]?.["Net Debt (EUR)"] ?? 0, 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-2 py-0.5 rounded">
                      {formatNumber(results.taxes?.[index]?.["Taxes (EUR)"] ?? 0, 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className={`px-2 py-0.5 rounded ${
                      (results.cashflow?.[index]?.["Cash Flow (EUR)"] || 0) >= 0 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {formatNumber(results.cashflow?.[index]?.["Cash Flow (EUR)"] ?? 0, 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className={`px-2 py-0.5 rounded font-medium ${
                      (results.cashflow?.[index]?.["Cumulative Cash Flow (EUR)"] || 0) >= 0 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {formatNumber(results.cashflow?.[index]?.["Cumulative Cash Flow (EUR)"] ?? 0, 0)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

}